import { clamp01, evaluateEasing } from "./easing";
import { interpolateMotionPath } from "./path";
import type { MotionKeyframe, MotionValue } from "./types";
export function parseMotionColor(
  color: string,
): [number, number, number, number] | null {
  const hex = /^#([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i.exec(color);
  if (hex) {
    let value = hex[1];
    if (value.length <= 4) value = [...value].map((c) => c + c).join("");
    return [
      parseInt(value.slice(0, 2), 16),
      parseInt(value.slice(2, 4), 16),
      parseInt(value.slice(4, 6), 16),
      value.length === 8 ? parseInt(value.slice(6), 16) / 255 : 1,
    ];
  }
  const rgb =
    /^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)(?:\s*,\s*(\d+(?:\.\d+)?|\.\d+))?\s*\)$/i.exec(
      color,
    );
  if (!rgb || /^rgba/i.test(color) !== (rgb[4] !== undefined)) return null;
  const values = [
    Number(rgb[1]),
    Number(rgb[2]),
    Number(rgb[3]),
    rgb[4] === undefined ? 1 : Number(rgb[4]),
  ] as [number, number, number, number];
  if (values.slice(0, 3).some((v) => v > 255) || values[3] > 1) return null;
  return values;
}
export function interpolateMotionValue(
  from: MotionValue,
  to: MotionValue,
  progress: number,
): MotionValue {
  if (typeof from === "number" && typeof to === "number")
    return from + (to - from) * progress;
  if (typeof from === "string" && typeof to === "string") {
    const a = parseMotionColor(from);
    const b = parseMotionColor(to);
    if (a && b) {
      const rgb = a
        .slice(0, 3)
        .map((v, i) => Math.round(v + (b[i] - v) * clamp01(progress)));
      const alpha = a[3] + (b[3] - a[3]) * clamp01(progress);
      return alpha === 1
        ? "#" + rgb.map((v) => v.toString(16).padStart(2, "0")).join("")
        : `rgba(${rgb.join(",")},${Math.round(alpha * 10000) / 10000})`;
    }
    const path = interpolateMotionPath(from, to, progress);
    if (path !== null) return path;
  }
  return progress < 1 ? from : to;
}
export function evaluateMotionKeyframes(
  frames: MotionKeyframe[],
  time: number,
): MotionValue {
  if (!frames.length) return 0;
  if (time <= frames[0].t) return frames[0].v;
  for (let i = 1; i < frames.length; i++)
    if (time < frames[i].t) {
      const previous = frames[i - 1];
      return interpolateMotionValue(
        previous.v,
        frames[i].v,
        evaluateEasing(
          previous.ease ?? frames[i].ease,
          (time - previous.t) / (frames[i].t - previous.t),
        ),
      );
    }
  return frames[frames.length - 1].v;
}
export function seededMotionRandom(seed: number, index = 0): number {
  let value = (Math.trunc(seed) ^ Math.imul(index + 1, 0x9e3779b9)) >>> 0;
  value = Math.imul(value ^ (value >>> 16), 0x21f0aaad);
  value = Math.imul(value ^ (value >>> 15), 0x735a2d97);
  return ((value ^ (value >>> 15)) >>> 0) / 4294967296;
}
export function seededMotionNoise(seed: number, time: number): number {
  const index = Math.floor(time);
  const p = time - index;
  const smooth = p * p * (3 - 2 * p);
  return (
    (seededMotionRandom(seed, index) +
      (seededMotionRandom(seed, index + 1) - seededMotionRandom(seed, index)) *
        smooth) *
      2 -
    1
  );
}
export function getMotionProperty(object: unknown, path: string): unknown {
  let value = object;
  for (const key of path.split(".")) {
    if (!value || typeof value !== "object") return undefined;
    value = (value as Record<string, unknown>)[key];
  }
  return value;
}
export function setMotionProperty(
  object: object,
  path: string,
  value: unknown,
): void {
  const keys = path.split(".");
  if (keys.some((k) => ["__proto__", "constructor", "prototype"].includes(k)))
    return;
  let parent = object as Record<string, unknown>;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!parent[key] || typeof parent[key] !== "object")
      parent[key] = /^\d+$/.test(keys[i + 1]) ? [] : {};
    parent = parent[key] as Record<string, unknown>;
  }
  parent[keys[keys.length - 1]] = value;
}
